/* Third party imports */
import { Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";

/* Local imports */
import Button from "../../../components/button";
import { ButtonSize, ButtonVariant } from "../../../components/button/types";
import { AuthContext } from "../../../contexts/AuthContext";
import { StepNameParameters, StepNameProps } from "./types";
import { useContext, useState } from "react";
import { useMutation } from "react-query";
import { updateLocale } from "../../../services/users.service";
import { sendFirstName } from "../../../services/onboarding.service";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ReactComponent as SearcIcon } from "../../../assets/search.svg";
import {
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from "libphonenumber-js";

export default function StepName({
  onNext,
  onSkip,
  initialData,
}: StepNameProps) {
  const { t, i18n } = useTranslation("onboarding");

  const { user, configurations } = useContext(AuthContext);

  const renderBirthdayField = configurations?.onboardingBirthday;
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [birthdayError, setBirthdayError] = useState<string | null>(null);
  const sendFirstNameMutation = useMutation(
    ["sendFirstName"],
    (firstName: string) => sendFirstName(firstName)
  );

  const updateLocaleMutation = useMutation(
    ["localeUpdate"],
    (mutationArgs: { locale: "de" | "en-US" }) =>
      updateLocale({ locale: mutationArgs.locale })
  );

  const getFormattedLang = (lang: string) => {
    if (lang === "english") {
      return "en";
    } else if (lang === "deutsch") {
      return "de";
    } else {
      return "en";
    }
  };

  const changeLanguageHandler = (lang: string) => {
    const formattedLang = getFormattedLang(lang);
    i18n.changeLanguage(formattedLang);
    localStorage.setItem("language", formattedLang);

    if (user?.cookieSettings?.preferencesCookiesAccepted) {
      const backendLang = lang === "english" ? "en-US" : "de";
      updateLocaleMutation.mutate({ locale: backendLang as "de" | "en-US" });
    }
  };

  const onSubmit = async ({
    firstName,
    lastName,
    day,
    month,
    year,
    mobilePhone,
    city,
    preferredLanguage,
  }: any) => {
    const hasDay = !!day?.trim();
    const hasMonth = !!month?.trim();
    const hasYear = !!year?.trim();

    if ((hasDay || hasMonth || hasYear) && !(hasDay && hasMonth && hasYear)) {
      setBirthdayError(
        t(
          "stepName.birthdayIncomplete",
          "Please enter complete birthday (Day, Month, Year) or leave all parts empty."
        )
      );
      return;
    }
    setBirthdayError(null);

    try {
      sendFirstNameMutation.mutate(firstName);
    } catch (error) {
      console.error("Failed to send first name", error);
    }

    if (day.length < 2) {
      day = `0${day}`;
    }
    if (month.length < 2) {
      month = `0${month}`;
    }

    const birthday =
      day && month && year
        ? new Date(`${year}-${month}-${day}`).toISOString()
        : null;

    onNext({
      firstName,
      lastName,
      birthday,
      mobilePhone,
      city,
      preferredLanguage,
    });
  };

  const validateGermanPhoneLength = (
    phoneNumber: string,
    countryCode: string
  ): boolean => {
    if (countryCode !== "DE") return true;

    const germanDialCode = "49";

    const digitsOnly = phoneNumber.replace(/\D/g, "");

    let nationalNumber = digitsOnly;
    if (digitsOnly.startsWith(germanDialCode)) {
      nationalNumber = digitsOnly.substring(germanDialCode.length);
    }

    if (nationalNumber.startsWith("0")) {
      nationalNumber = nationalNumber.substring(1);
    }

    if (nationalNumber.charAt(0) !== "1") {
      return false;
    }

    return nationalNumber.length === 10 || nationalNumber.length === 11;
  };

  if (user?.displayName) onSkip();
  const getInitialValue = (
    key: keyof StepNameParameters,
    fallback: any = ""
  ) => {
    return initialData && key in initialData
      ? initialData[key] ?? fallback
      : fallback;
  };

  return (
    <Formik
      initialValues={{
        firstName: getInitialValue("firstName"),
        lastName: getInitialValue("lastName"),
        day: initialData?.birthday
          ? new Date(initialData.birthday).getDate().toString()
          : "",
        month: initialData?.birthday
          ? (new Date(initialData.birthday).getMonth() + 1).toString()
          : "", // Month is 0-indexed
        year: initialData?.birthday
          ? new Date(initialData.birthday).getFullYear().toString()
          : "",
        mobilePhone: getInitialValue("mobilePhone"),
        city: getInitialValue("city"),
        preferredLanguage: getInitialValue(
          "preferredLanguage",
          i18n.language === "en" ? "english" : "deutsch"
        ),
      }}
      onSubmit={onSubmit}
    >
      {({ values, handleChange, setFieldValue }) => (
        <div className="w-full">
          <div className="flex flex-col">
            <h1 className="text-xl font-bold leading-7 mb-1">
              {t("stepName.title")}
            </h1>
            <h2 className="text-base mt-2">{t("stepName.subtitle")}</h2>
            <Form className="w-full  mt-4">
              <div className="flex flex-col w-full">
                <label className="text-base mb-1">
                  {t("stepName.firstName")}*
                </label>
                <Field
                  type="text"
                  placeholder={t("stepName.writeHere")}
                  className="border rounded-lg px-4 py-3.5 text-base w-full border-black"
                  id="firstName"
                  name="firstName"
                  value={values?.firstName}
                  onChange={handleChange}
                  required
                />
                <label className="text-base mb-1 mt-4">
                  {t("stepName.lastName")}*
                </label>
                <Field
                  type="text"
                  placeholder={t("stepName.writeHere")}
                  className="border rounded-lg  px-4 py-3.5  text-base  w-full border-black"
                  id="lastName"
                  name="lastName"
                  value={values?.lastName}
                  onChange={handleChange}
                  required
                />
                {renderBirthdayField && (
                  <>
                    <label
                      htmlFor="date-picker"
                      className="text-base mb-1 mt-4"
                    >
                      {t("stepName.birthday")}
                    </label>
                    <div className="w-full mb-4 flex flex-row grid grid-cols-3 gap-2">
                      <input
                        placeholder={t("stepName.day") || ""}
                        type="number"
                        min="1"
                        max="31"
                        value={values?.day}
                        className="border border-black rounded-lg px-4 py-3.5 text-base"
                        onChange={(data) =>
                          setFieldValue("day", data?.target?.value)
                        }
                      />

                      <input
                        placeholder={t("stepName.month") || ""}
                        type="number"
                        min="1"
                        max="12"
                        value={values?.month}
                        className="border border-black rounded-lg px-4 py-3.5 text-base"
                        onChange={(data) =>
                          setFieldValue("month", data?.target?.value)
                        }
                      />
                      <input
                        placeholder={t("stepName.year") || ""}
                        type="number"
                        min="1900"
                        max={new Date(Date.now()).getFullYear().toString()}
                        value={values?.year}
                        className="border border-black rounded-lg px-4 py-3.5 text-base"
                        onChange={(data) =>
                          setFieldValue("year", data?.target?.value)
                        }
                      />
                    </div>
                    {birthdayError && (
                      <p className="text-red-500 text-sm mt-1">
                        {birthdayError}
                      </p>
                    )}
                  </>
                )}

                <label className="text-base mb-1 mt-4">
                  {t("stepName.mobilePhone")}
                </label>
                <div className="border border-black rounded-lg w-full ">
                  <PhoneInput
                    country={"de"}
                    value={values.mobilePhone}
                    onChange={(value, country, event, formattedValue) => {
                      if (!value) {
                        setFieldValue("mobilePhone", "");
                        setIsPhoneValid(true);
                        setIsFormDirty(true);
                        return;
                      }

                      setFieldValue("mobilePhone", value);
                      setIsFormDirty(true);

                      try {
                        const dialCode = (country as any)?.dialCode || "";

                        let nationalPart = "";

                        if (value.startsWith(dialCode)) {
                          nationalPart = value.substring(dialCode.length);
                        }

                        if (nationalPart.startsWith("0")) {
                          const correctedValue =
                            dialCode + nationalPart.substring(1);

                          console.log("Prevented leading zero:", {
                            originalValue: value,
                            correctedValue,
                          });

                          setFieldValue("mobilePhone", correctedValue);

                          value = correctedValue;
                        } else {
                          setFieldValue("mobilePhone", value);
                        }

                        setIsFormDirty(true);

                        const countryCode =
                          (country as any)?.countryCode?.toUpperCase() || "";
                        const fullNumber = value.startsWith("+")
                          ? value
                          : `+${value}`;

                        const isValid = isValidPhoneNumber(
                          fullNumber,
                          countryCode
                        );
                        const lengthValidation = validatePhoneNumberLength(
                          fullNumber,
                          countryCode
                        );
                        const hasValidLength =
                          lengthValidation !== "TOO_SHORT" &&
                          lengthValidation !== "TOO_LONG" &&
                          lengthValidation !== "INVALID_LENGTH" &&
                          lengthValidation !== "NOT_A_NUMBER" &&
                          lengthValidation !== "INVALID_COUNTRY";

                        const isPossible = isPossiblePhoneNumber(
                          fullNumber,
                          countryCode
                        );
                        const hasValidGermanLength = validateGermanPhoneLength(
                          fullNumber,
                          countryCode
                        );
                        const isGermanNumberValid =
                          countryCode !== "DE" || hasValidGermanLength;

                        setIsPhoneValid(
                          isValid &&
                            hasValidLength &&
                            isPossible &&
                            isGermanNumberValid
                        );
                      } catch (error) {
                        console.log("Phone validation error:", error);
                        setIsPhoneValid(false);
                      }
                    }}
                    countryCodeEditable={true}
                    enableSearch={true}
                    disableSearchIcon={false}
                    searchPlaceholder={t("stepName.search") || "Search"}
                    placeholder={
                      t("stepName.mobilePhonePlaceHolder") || "+49 123 456 7890"
                    }
                    containerStyle={{
                      width: "100%",
                      height: "100%",
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "auto",
                      padding: "0.875rem 0.875rem 0.875rem 52px",
                      fontSize: "1rem",
                      lineHeight: "1.5",
                      borderRadius: "0.5rem",
                      background: "transparent",
                      outline: "none",
                      fontFamily: "inherit",
                    }}
                    buttonStyle={{
                      border: "none",
                      borderRadius: "0",
                      background: "transparent",
                      padding: "2px",
                    }}
                    dropdownStyle={{
                      border: "1px solid black",
                      borderRadius: "0.5rem",
                      marginTop: "5px",
                      fontFamily: "inherit", // Ensures it matches your app font
                    }}
                  />
                  <style>
                    {`
              /* 🔹 Remove the search icon completely */
            .react-tel-input .search-emoji {
  display: none !important;
  visibility: hidden !important;
  width: 0 !important;
  height: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

              /* 🔹 Apply Figtree font to all input fields */
              .react-tel-input input,
              .react-tel-input .search-box,
              .react-tel-input .country-list {
                font-family: 'Figtree', source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace !important;
                font-size: 1rem !important;
              }

      /* 🔹 Style the country list with horizontal padding */
.react-tel-input .country-list {
  padding-left: 5px !important; 
  padding-right: 5px !important; 
  display: flex;
  flex-direction: column; /* Keep the list vertical */
}

              /* 🔹 Style the search box */
              .react-tel-input .search-box {
                border-radius: 0.5rem !important; 
                padding: 0.5rem !important;
                background-color: white !important;
            }
                .react-tel-input input:focus {
  border: 1px solid #007bff !important; /* Change color to your desired focus color (e.g., blue) */
}
  `}
                  </style>
                </div>
                {!isPhoneValid && (
                  <p className="text-red-500 text-sm mt-1">
                    {t("stepName.invalidPhone") || "Invalid phone number"}
                  </p>
                )}
                <label className="text-base mb-1 mt-4">
                  {t("stepName.city")}
                </label>
                <Field
                  type="text"
                  placeholder={t("stepName.cityPlaceHolder")}
                  className="border rounded-lg px-4 py-3.5 text-base w-full border-black"
                  id="city"
                  name="city"
                  value={values?.city}
                  onChange={handleChange}
                />

                <label className="text-base mb-1 mt-4">
                  {t("stepName.preferredLanguage")}*
                </label>
                <Field
                  as="select"
                  className="border rounded-lg px-4 py-3.5 text-base w-full border-black"
                  id="preferredLanguage"
                  name="preferredLanguage"
                  onChange={(e: any) => {
                    const selectedLanguage = e.target.value;
                    setFieldValue("preferredLanguage", selectedLanguage);
                    changeLanguageHandler(selectedLanguage);
                  }}
                  value={values.preferredLanguage}
                  required
                >
                  <option value="english">English</option>
                  <option value="deutsch">Deutsch</option>
                </Field>
              </div>
              <div className="w-full flex justify-between	mt-6">
                <Button
                  className="w-full"
                  type="submit"
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.MEDIUM}
                  disabled={!!values.mobilePhone && !isPhoneValid}
                >
                  {t("stepName.next")}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </Formik>
  );
}
